import { CheckCircle2, Info, XCircle } from 'lucide-react'
import { useSyncExternalStore } from 'react'

import { cn } from '@/lib/cn'

type Tone = 'info' | 'success' | 'danger'

interface ToastItem {
  id: number
  message: string
  tone: Tone
}

let nextId = 1
let items: ToastItem[] = []
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function dismiss(id: number) {
  items = items.filter((item) => item.id !== id)
  emit()
}

export function toast(message: string, tone: Tone = 'info') {
  const id = nextId++
  items = [...items.slice(-2), { id, message, tone }]
  emit()
  window.setTimeout(() => dismiss(id), 4500)
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const toneStyles: Record<Tone, string> = {
  info: 'text-white/80',
  success: 'text-success-bright',
  danger: 'text-danger-bright',
}

const toneIcons: Record<Tone, typeof Info> = {
  info: Info,
  success: CheckCircle2,
  danger: XCircle,
}

/* Single portal-free toast region; mounted once inside the app shell. */
export function Toaster() {
  const current = useSyncExternalStore(
    subscribe,
    () => items,
    () => items,
  )
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-[calc(5.5rem+var(--safe-bottom))] z-50 grid justify-items-center gap-2 px-5"
    >
      {current.map(({ id, message, tone }) => {
        const Icon = toneIcons[tone]
        return (
          <button
            key={id}
            type="button"
            onClick={() => dismiss(id)}
            className={cn(
              'pointer-events-auto flex max-w-md min-w-0 animate-fade-up items-center gap-2.5 rounded-card bg-pine px-4 py-3 text-left text-sm font-semibold shadow-overlay ring-1 ring-white/10',
              toneStyles[tone],
            )}
          >
            <Icon className="size-4 shrink-0" />
            <span className="text-white">{message}</span>
          </button>
        )
      })}
    </div>
  )
}
